import { useActivity } from '@/OneLiners';
import {
    ActionStatusHelper,
    useAppEnum,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
    DOMAIN_CONFIGS,
} from "@/Shared";

import { ActivityFormViewModel } from '../ViewModels';
import { useActivityEnricher } from "./useActivityEnricher";
import { ActivityModel } from "../Models";
import { ActivityValidationSchema } from '../ActivityFormValidation';


/**
 * =====================================================
 * Hook: useActivityForm
 * =====================================================
 *
 * Description:
 * Orchestration hook responsible for managing the Activity form lifecycle.
 *
 * This hook acts as the bridge between:
 * - domain data (Activity, permissions)
 * - enrichment layer (users, tasks, computed fields)
 * - UI form logic (Formik, submission, navigation)
 *
 * Responsibilities:
 * - Fetch activity data (create / edit mode)
 * - Resolve permissions and access rights
 * - Enrich activity data before exposing it to the UI
 * - Build and expose the ActivityFormViewModel
 * - Handle form submission (create / update)
 * - Manage global loading and error states
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export const useActivityForm = (
    props : DomainHookProps
): DomainFormHook => {
    const {extraArgs, permissionHook } = props;
    const { task } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Activity;


    /**
    * =====================================================
    * Activity Domain Operations
    * =====================================================
    *
    * Provides:
    * - CRUD actions
    * - Current entity fetching
    * - Backend state & responses
    */
    const activityHook = useActivity();


    /**
    * =====================================================
    * Domain Enums
    * =====================================================
    *
    * Activity-related enums exposed to the UI layer
    * and used by the ViewModel.
    */
    const { ActivityEnums } = useAppEnum();


    /**
     * =====================================================
     * Activity Enrichment Layer
     * =====================================================
     *
     * Enriches activity data with:
     * - users
     * - tasks
     * - computed / audit fields
     *
     * Required to build a complete ViewModel.
     */
    const {
        userMap,
        taskMap,
        enricherResponse,
        getEnrichedActivity,
    } = useActivityEnricher();

    /**
     * =====================================================
     * Global Initialization Response
     * =====================================================
     *
     * Aggregates loading and error states from:
     * - Activity domain
     * - Permission fetching
     * - Enrichment layer
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            activityHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: activityHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycle = useFormLifeCycle({
        response, 
        domainType, 
        props, 
        entityHook : activityHook,
        buildVM : (currentEntity : ActivityModel) => {
            return new ActivityFormViewModel({
                viewEnums: ActivityEnums,
                taskMap,
                userMap,
                activity: getEnrichedActivity(currentEntity),
                task
            })
        } 
    });

    return {
        ...lifeCycle,
        viewEnums: ActivityEnums,
        ValidationSchema: ActivityValidationSchema(ActivityEnums),
    }
}